<template>
  <v-app>
    <v-container fluid>
      <!-- ================== SEARCH PANEL ================== -->
      <v-expansion-panels v-model="panel" focusable>
        <v-expansion-panel>
          <v-expansion-panel-title color="blue-grey-lighten-4" class="panel-header">
            Search Form
          </v-expansion-panel-title>
          <v-expansion-panel-text>
            <v-form @submit.prevent="submit">
              <v-row class="pa-2" dense>
                <v-col lg="2" md="4" cols="12">
                  <v-autocomplete
                    label="Customer"
                    variant="outlined"
                    density="compact"
                    bg-color="grey-lighten-4"
                    v-model="customer_id.value.value"
                    :items="getCustomerMapList"
                    :item-title="(item) => item ? `${item.customer.customer_name || item.customer.customer_owner_name || ''} - ${item.customer.code}` : ''"
                    item-value="customer_id"
                    :error-messages="customer_id.errorMessage.value"
                    hide-details="auto"
                    autocomplete="off"
                    clearable
                  ></v-autocomplete>
                </v-col>
                <v-col lg="2" md="4" cols="12">
                  <v-autocomplete 
                    label="Sales Person"
                    density="compact"
                    variant="outlined" 
                    bg-color="grey-lighten-4"
                    :items="getBrandList"
                    v-model="sales_by.value.value"
                    :item-title="item => item && item.name"                                                  
                    item-value="id"
                    :error-messages="sales_by.errorMessage.value"
                    autocomplete="off"
                    clearable
                   >
                  </v-autocomplete>
                </v-col>

                <v-col lg="2" md="4" cols="12">
                  <v-date-input
                    label="From Date"
                    density="compact"
                    single-line
                    prepend-icon=""
                    prepend-inner-icon="mdi-calendar"
                    variant="outlined"
                    bg-color="grey-lighten-4"
                    v-model="from_date.value.value"
                    :error-messages="from_date.errorMessage.value"
                    hide-details="auto"
                    autocomplete="off"
                    clearable
                  ></v-date-input>
                </v-col>

                <v-col lg="2" md="4" cols="12">
                  <v-date-input
                    label="To Date"
                    density="compact"
                    single-line
                    prepend-icon=""
                    prepend-inner-icon="mdi-calendar"
                    variant="outlined"
                    bg-color="grey-lighten-4"
                    v-model="to_date.value.value"
                    :error-messages="to_date.errorMessage.value"
                    hide-details="auto"
                    autocomplete="off"
                    clearable
                  ></v-date-input>
                </v-col>

                <v-col lg="2" md="4" cols="12">
                  <v-btn class="bg-brand" type="submit" ref="searchButton">Search</v-btn>
                </v-col>
              </v-row>
            </v-form>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-container>

    <!-- ================== REPORT PANEL ================== -->
    <v-container fluid class="mt-4">
      <v-expansion-panels v-model="panelReport" focusable>
        <v-expansion-panel>
          <v-expansion-panel-title color="blue-grey-lighten-4" class="panel-header">
            Sales Statement Reports
          </v-expansion-panel-title>
          <v-expansion-panel-text>

            <!-- ================== Filters + Export ================== -->
            <v-row class="py-2" align="center">
              <v-col md="4" cols="12">
                <v-text-field
                  v-model="search"
                  append-inner-icon="mdi-magnify"
                  label="Search"
                  single-line
                  hide-details
                  density="compact"
                  variant="outlined"
                  bg-color="grey-lighten-4"
                  clearable
                ></v-text-field>
              </v-col>

              <v-col md="5" cols="12">
                <v-btn-group variant="outlined" density="compact" size="x-small" divided>
                  <v-btn prepend-icon="mdi mdi-download" variant="flat" color="success" @click="exportToCSV">CSV</v-btn>
                  <v-btn prepend-icon="mdi mdi-download" variant="flat" color="primary" @click="exportToExcel">EXCEL</v-btn>
                  <v-btn prepend-icon="mdi mdi-download" variant="flat" color="red" @click="exportToPDF">PDF</v-btn>
                </v-btn-group>
              </v-col>
            </v-row>

            <div class="mb-6">
                <v-accordion multiple>
                  <v-accordion-item
                    v-for="(group, index) in groupedData"
                    :key="index"
                    :title="group.invoice_code"
                  >
                    <v-card elevation="0">
                      <v-data-table
                        :headers="headers"
                        :items="group.items"
                        :items-per-page="-1"
                        :search="search"
                        :hover="true"
                        class="elevation-1"
                        density="comfortable"
                        hide-default-footer
                      >
                        <template #item.index="{ index }">
                            {{ ++index }}
                        </template>

                        <template #item.ordered_qty="{ item }">
                          <strong>{{ item.ordered_qty }}</strong>
                        </template>

                        <template #item.delivered_qty="{ item }">
                          <strong class="text-blue">{{ item.delivered_qty }}</strong>
                        </template>

                        <template #item.gate_pass_qty="{ item }">
                          <strong class="text-green">{{ item.gate_pass_qty }}</strong>
                        </template>

                        <!-- FOOTER ROW -->
                        <template #tfoot density="comfortable">
                            <tr class="font-weight-bold">
                                <td colspan="5" class="pa-2 border-t border-b text-center">Totals:</td>
                                <td class="border-t border-b text-left">{{ sum(group.items, 'ordered_qty') }}</td>
                                <td class="border-t border-b text-left text-blue">{{ sum(group.items, 'delivered_qty') }}</td>
                                <td class="border-t border-b text-left text-green">{{ sum(group.items, 'gate_pass_qty') }}</td>
                            </tr>
                        </template>
                       
                      </v-data-table>
                    </v-card>
                  </v-accordion-item>
                </v-accordion>
            </div>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-container>
  </v-app>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useStore } from "vuex";
import { useField, useForm } from "vee-validate";
import * as yup from "yup";
import Axios from "axios";
import formattedDate from "@/plugins/dateFormatter";
import { getSalesStatementReports } from "@/store/api.js";

const panel = ref([0]);
const panelReport = ref([0]);
const activeTab = ref("flat");
const search = ref("");

// --------------------- Form fields ---------------------
const { handleSubmit } = useForm({
  validationSchema: yup.object({
    from_date: yup.date().nullable().label("From Date"),
    to_date: yup.date().nullable().label("To Date"),
  }),
});
const from_date = useField("from_date");
const to_date = useField("to_date");
const customer_id = useField("customer_id");
const sales_by = useField("sales_by");
const invoice_code = useField("invoice_code");

const store = useStore();
const reportData = ref([]);
const summary = ref({});
const filters = ref({ with_details: false });

// --------------------- Customer list ---------------------
const getCustomerMapList = computed(() => store.getters.getCustomerMapList || []);
const getBrandList = computed(() => store.getters.getBrandList || []);


// --------------------- Submit Function ---------------------
const submit = handleSubmit(async (values) => {

   filters.value = {
    customer_id: customer_id.value.value,
    sales_by: sales_by.value.value,
    invoice_code: invoice_code.value.value,
    from_date: formattedDate(from_date.value.value),
    to_date: formattedDate(to_date.value.value)
  };

  try {
    const { data } = await Axios.post(getSalesStatementReports, filters.value);
    reportData.value = data.data;
  } catch (err) {
    console.error(err);
  }

});

// --------------------- OPTION A: Flat Table ---------------------
const groupedData = computed(() => {
  const groups = {};

  reportData.value.forEach((item) => {
    if (!groups[item.sale_order_id]) {
      groups[item.sale_order_id] = {
        invoice_code: item.sale_order_code,
        items: [],
      };
    }

    groups[item.sale_order_id].items.push(item);
  });

  return Object.values(groups);
});

const headers = [
    { title: "#SL", align: "center", key: "index", width: "5%", sortable: false, },
    { title: "SO Invoice", align: "center", key: "sale_order_code", sortable: false, },
    { title: "Product ID", key: "product_code", width: 120 },
    { title: "Product Name", key: "product_name", width: 120 },
    { title: "Category", key: "category" },
    { title: "Ordered Qty", key: "ordered_qty", width: 140 },
    { title: "Delivered Qty", key: "delivered_qty", width: 140 },
    { title: "Gate Pass Qty", key: "gate_pass_qty", width: 150 },
];

/* ---------------- SUM UTILITY ---------------- */
const sum = (items, key) => {
  return items.reduce((acc, cur) => acc + Number(cur[key] || 0), 0);
};

// Dispatch getCustomerMapList on mount
onMounted(() => {
  store.dispatch('getCustomerMapList');
  store.dispatch('getBrandList');
});

// --------------------- Export Functions (Stub, can implement real) ---------------------
function exportToCSV() { console.log("Export CSV"); }
function exportToExcel() { console.log("Export Excel"); }
function exportToPDF() { console.log("Export PDF"); }

</script>
